import "./filter-module.css";

import { CurveEditor, type CurvePoint } from "./curve-editor.ts";
import { Selector, GLYPHS } from "./selector.ts";
import { Knob } from "./knob.ts";

// FilterModule — the synth's filter section. Layout:
//   [FILTER ····················· LP HP BP NT]
//   [        FILTER RESPONSE CURVE (handle)    ]
//   [   CUTOFF   ·   RESONANCE   ·   ENV AMT   ]
//
// The curve is the visual centerpiece of the mid section per design.md.
// A single XY handle on the curve drives cutoff (X, log Hz) and
// resonance (Y, log Q). Mode is picked from the right-side selector.
// Three small knobs underneath give direct numeric edit for each axis
// plus the filter-envelope-modulation amount (a separate engine param).

export type FilterMode = "lp" | "hp" | "bp" | "notch";

const MODE_TO_INDEX: Record<FilterMode, number> = {
  lp: 0,
  hp: 1,
  bp: 2,
  notch: 3,
};

const MIN_FREQ = 20;
const MAX_FREQ = 20000;
const LOG_MIN = Math.log(MIN_FREQ);
const LOG_RANGE = Math.log(MAX_FREQ) - LOG_MIN;
const MIN_Q = 0.5;
const MAX_Q = 20;
const LOG_Q_MIN = Math.log(MIN_Q);
const LOG_Q_RANGE = Math.log(MAX_Q) - LOG_Q_MIN;

function freqToX(hz: number): number {
  return (Math.log(Math.max(hz, MIN_FREQ)) - LOG_MIN) / LOG_RANGE;
}
function xToFreq(x: number): number {
  return Math.exp(LOG_MIN + x * LOG_RANGE);
}
function qToY(q: number): number {
  return (Math.log(Math.max(q, MIN_Q)) - LOG_Q_MIN) / LOG_Q_RANGE;
}
function yToQ(y: number): number {
  return Math.exp(LOG_Q_MIN + y * LOG_Q_RANGE);
}

export interface FilterState {
  cutoffHz: number;
  resonance: number;
  mode: FilterMode;
  drive: number;
}

export interface FilterModuleOptions {
  container: HTMLElement;
  setParam: (path: string, value: number) => void;
  setDiscrete: (path: string, value: number) => void;
  initial?: Partial<FilterState>;
}

export class FilterModule {
  private root: HTMLDivElement;
  private mode: FilterMode;
  private cutoffHz: number;
  private resonance: number;
  private drive: number;
  private editor: CurveEditor;
  private cutoffKnob: Knob;
  private resKnob: Knob;
  private driveKnob!: Knob;
  private modeSelector!: Selector<FilterMode>;
  private setParam: FilterModuleOptions["setParam"];
  private setDiscrete: FilterModuleOptions["setDiscrete"];

  constructor(opts: FilterModuleOptions) {
    const init = opts.initial ?? {};
    this.mode = init.mode ?? "lp";
    this.cutoffHz = init.cutoffHz ?? 1500;
    this.resonance = init.resonance ?? 0.707;
    this.drive = init.drive ?? 0;
    this.setParam = opts.setParam;
    this.setDiscrete = opts.setDiscrete;

    this.root = document.createElement("div");
    this.root.className = "filter-module";

    // Header — title + mode selector on the right.
    const header = document.createElement("div");
    header.className = "filter-module-header";

    const title = document.createElement("span");
    title.className = "filter-module-title";
    title.textContent = "filter";
    header.append(title);

    const modeHost = document.createElement("div");
    modeHost.className = "filter-module-mode";
    header.append(modeHost);
    this.modeSelector = new Selector<FilterMode>({
      container: modeHost,
      options: [
        { value: "lp", glyph: GLYPHS.filterLowpass, label: "lowpass" },
        { value: "hp", glyph: GLYPHS.filterHighpass, label: "highpass" },
        { value: "bp", glyph: GLYPHS.filterBandpass, label: "bandpass" },
        { value: "notch", glyph: GLYPHS.filterNotch, label: "notch" },
      ],
      value: this.mode,
      onChange: (mode) => {
        this.mode = mode;
        this.setDiscrete("filter.mode", MODE_TO_INDEX[mode]);
        this.editor.redraw();
      },
    });

    this.root.append(header);

    // Curve editor — one XY handle representing cutoff/resonance.
    const curveHost = document.createElement("div");
    curveHost.className = "filter-module-curve";
    this.root.append(curveHost);

    const pts: CurvePoint[] = [
      {
        x: freqToX(this.cutoffHz),
        y: qToY(this.resonance),
        drag: "xy",
        label: "cutoff · resonance",
      },
    ];

    this.editor = new CurveEditor({
      container: curveHost,
      width: 360,
      height: 140,
      fluid: true,
      points: pts,
      draw: (ctx, w, h, p) => this.drawResponse(ctx, w, h, p),
      onChange: (p) => {
        const handle = p[0]!;
        this.cutoffHz = xToFreq(handle.x);
        this.resonance = yToQ(handle.y);
        // Keep the knobs in sync without re-firing their own onChange.
        this.cutoffKnob.setValue(this.cutoffHz);
        this.resKnob.setValue(this.resonance);
        this.setParam("filter.cutoff_hz", this.cutoffHz);
        this.setParam("filter.resonance", this.resonance);
      },
    });

    // Knob row — cutoff / resonance / env amount.
    const knobs = document.createElement("div");
    knobs.className = "filter-module-knobs";
    this.root.append(knobs);

    this.cutoffKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_FREQ,
      max: MAX_FREQ,
      default: 1500,
      value: this.cutoffHz,
      taper: "log",
      label: "cutoff",
      format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v.toFixed(0)}`),
      onChange: (v) => {
        this.cutoffHz = v;
        this.setParam("filter.cutoff_hz", v);
        this.syncHandleFromKnobs();
      },
    });

    this.resKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_Q,
      max: MAX_Q,
      default: 0.707,
      value: this.resonance,
      taper: "log",
      label: "res",
      format: (v) => `q ${v.toFixed(1)}`,
      onChange: (v) => {
        this.resonance = v;
        this.setParam("filter.resonance", v);
        this.syncHandleFromKnobs();
      },
    });

    // Drive — pre-filter input gain that pushes the filter into saturation.
    // Drive — pre-filter saturation; pushes the input into a tanh
    // soft-clip before the SVF integrators see it.
    this.driveKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0,
      value: this.drive,
      label: "drive",
      format: (v) => v.toFixed(2),
      onChange: (v) => {
        this.drive = v;
        this.setParam("filter.drive", v);
      },
    });

    opts.container.append(this.root);
  }

  getState(): FilterState {
    return {
      cutoffHz: this.cutoffKnob.current,
      resonance: this.resKnob.current,
      mode: this.modeSelector.getValue(),
      drive: this.driveKnob.current,
    };
  }

  setState(s: FilterState): void {
    this.cutoffKnob.setValue(s.cutoffHz);
    this.resKnob.setValue(s.resonance);
    this.modeSelector.select(s.mode);
    this.driveKnob.setValue(s.drive);
  }

  /** Update the curve editor's handle position from the current cutoff /
   *  resonance state (used after a knob drag). */
  private syncHandleFromKnobs(): void {
    const p = this.editor.getPoints();
    if (!p[0]) return;
    p[0].x = freqToX(this.cutoffHz);
    p[0].y = qToY(this.resonance);
    this.editor.setPoints(p);
  }

  /** Render the filter response curve. Y is height — 0 at bottom, 1 at
   *  top, with the visible curve sitting between the two. The
   *  approximations below match standard 2-pole biquad shapes well enough
   *  for visual feedback; they're not used for actual audio. */
  private drawResponse(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    _pts: CurvePoint[],
  ): void {
    void _pts;
    const cutoff = this.cutoffHz;
    const q = this.resonance;
    const mode = this.mode;

    // Faint vertical guides at 100Hz, 1kHz, 10kHz.
    ctx.save();
    ctx.strokeStyle = "rgba(56, 189, 248, 0.08)";
    ctx.lineWidth = 1;
    for (const hz of [100, 1000, 10000]) {
      const x = freqToX(hz) * w;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    ctx.restore();

    // Sample the magnitude response across log-spaced frequencies.
    ctx.save();
    ctx.shadowColor = "rgba(56, 189, 248, 0.6)";
    ctx.shadowBlur = 14;
    ctx.strokeStyle = "rgba(125, 211, 252, 0.92)";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    const samples = 200;
    for (let i = 0; i <= samples; i++) {
      const u = i / samples;
      const f = xToFreq(u);
      const mag = filterMagnitude(mode, f, cutoff, q);
      // mag in [0, ~Q]; clamp to a display range. Map mag=1 to mid-height
      // and let resonance peak push toward top, dips toward bottom.
      const yNorm = clamp(mag / 1.6, 0, 1);
      const yPx = (1 - yNorm) * h;
      const xPx = u * w;
      if (i === 0) ctx.moveTo(xPx, yPx);
      else ctx.lineTo(xPx, yPx);
    }
    ctx.stroke();
    ctx.restore();
  }

  destroy(): void {
    this.root.remove();
  }
}

/** Approximate magnitude response for a 2-pole filter at frequency `f`,
 *  given cutoff `fc` and resonance Q. Returns a normalized 0..~Q value
 *  suitable for a visual response curve — not audio-quality. */
function filterMagnitude(mode: FilterMode, f: number, fc: number, q: number): number {
  const r = f / fc;
  // Resonance peak/dip width: tighter as Q increases.
  switch (mode) {
    case "lp": {
      const base = 1 / Math.sqrt(1 + Math.pow(r, 4));
      const peak = Math.exp(-Math.pow((Math.log(r) * q) / 2, 2)) * Math.max(0, q - 0.707);
      return base + peak * 0.5;
    }
    case "hp": {
      const base = (r * r) / Math.sqrt(1 + Math.pow(r, 4));
      const peak = Math.exp(-Math.pow((Math.log(r) * q) / 2, 2)) * Math.max(0, q - 0.707);
      return base + peak * 0.5;
    }
    case "bp": {
      // Centred on fc; width 1/Q in log space.
      const width = 1 / Math.max(q, 0.5);
      const bell = Math.exp(-Math.pow(Math.log(r) / width, 2));
      // Slight extra "peak" with high Q for the resonance ridge.
      return bell * (0.85 + Math.min(0.6, q * 0.04));
    }
    case "notch": {
      const width = 1 / Math.max(q, 0.5);
      const bell = Math.exp(-Math.pow(Math.log(r) / width, 2));
      return 1 - bell * 0.95;
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
